<template>
    <!--
        "New Patient Packet · Section 3 of 8" + a bar, rendered under the practice header on every
        form of a packet. Purely informational: it tells the patient there is more to come, which is
        the whole reason this exists (someone filled section 1, saw "Submit", and stopped).

        The packet NAME is its own {{ }} node and never goes through t() — systemTranslations
        interpolates with String.replace, which would treat `$&`/`$1` inside a practice's packet name
        as capture-group syntax. See libraries/packet-progress.js.
    -->
    <div class="packet-progress-strip" data-test="packet-progress-strip">
        <div class="packet-progress-text">
            <span v-if="displayName" class="packet-name">{{ displayName }}</span><span v-if="displayName" class="packet-sep"> &middot; </span><span class="packet-section">{{ sectionText }}</span>
        </div>
        <div class="packet-progress-track">
            <div class="packet-progress-fill" :style="{ width: fillWidth }"></div>
        </div>
    </div>
</template>

<script>
import { t } from '../utils/systemTranslations';

export default {
    name: 'PacketProgressStrip',

    props: {
        packetName: { type: String, default: '' },
        step: { type: Number, required: true },
        total: { type: Number, required: true },
        language: { type: String, default: 'en' }
    },

    computed: {
        /** Blank / whitespace-only names render nothing at all — no stray separator. */
        displayName() {
            return (this.packetName || '').trim();
        },

        sectionText() {
            return t('sectionOf', this.language, { step: this.step, total: this.total });
        },

        /**
         * Filled portion = sections ALREADY finished, so section 1 of 8 starts empty and the bar is
         * full only once the last section has been submitted (the patient is gone by then, but the
         * alternative — "1 of 8" showing 12.5% done before a single answer — overstates progress).
         */
        fillWidth() {
            if (!this.total || this.total < 1) return '0%';
            const pct = ((this.step - 1) / this.total) * 100;
            const clamped = Math.max(0, Math.min(100, pct));
            return `${clamped}%`;
        }
    }
};
</script>

<style lang="scss" scoped>
.packet-progress-strip {
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    padding: 12px 16px;
    margin: 0 2rem 16px 2rem;
    text-align: center;
    box-sizing: border-box;
}

.packet-progress-text {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #334155;
    line-height: 1.4;
    // Long packet names must WRAP at 390px, never push the card sideways.
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
}

.packet-sep {
    color: #94a3b8;
}

.packet-section {
    font-weight: 500;
    color: #64748b;
    white-space: nowrap;
}

.packet-progress-track {
    margin-top: 10px;
    height: 6px;
    border-radius: 999px;
    background-color: #e2e8f0;
    overflow: hidden;
}

.packet-progress-fill {
    height: 100%;
    border-radius: 999px;
    background-color: #0f7259;
    transition: width 0.3s ease;
}

@media (max-width: 768px) {
    .packet-progress-strip {
        margin: 0 1rem 12px 1rem;
    }
}

@media (max-width: 480px) {
    .packet-progress-strip {
        margin: 0 8px 8px 8px;
        padding: 10px 12px;
    }

    .packet-progress-text {
        font-size: 0.875rem;
    }
}
</style>
